import { observer } from "mobx-react";
import { MultiValue } from "react-select";
import Drawer from "../../../../../components/drawer/drawer";
import DefaultDrawerLayout from "../../../../../components/drawer/layouts/default-drawer-layout";
import FontHeading5 from "../../../../../components/fonts/font-heading5/font-heading5";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import { FormField } from "../../../../../components/form-field/form-field";
import IconFunnel from "../../../../../components/icons/icon-funnel/icon-funnel";
import { IOption } from "../../../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import useValidationGroup from "../../../../../hooks/validation-group/validation-group-hook";
import ShipmentListFiltersStore from "../../../stores/shipments-list-filters-store";
import { Button } from "../../../../../components/inputs/button/button";
import SelectDropdownMulti from "../../../../../components/inputs/select-dropdown-multi/select-dropdown-multi";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import shipmentsStore from "../../../stores/shipments-store";
import "./shipments-list-filters-drawer.scss";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { SHIPMENT_STATUS_ENUM } from "../../../../manage-returns/rma-page/rma-types";
import DateRangePickerV2, {IDateRangeISO} from "../../../../../components/date-range-picker-v2/date-range-picker-v2";
import {parseIsoDateRange} from "../../../../../utilities/date-utilities";

interface IProps {
    onClose: () => void;
    shipmentsListFiltersStore: ShipmentListFiltersStore;
}

const ShipmentsListFiltersDrawer: React.FC<IProps> = observer(({ onClose, shipmentsListFiltersStore }) => {
    const vGroup = useValidationGroup();
    const {getTranslation} = useDashboardTranslation();
    const scope = 'dashboard-returns-rma-card';

    const shipmentStatusOptions = shipmentsListFiltersStore.getShipmentStatusOptions(getTranslation, scope);
    const selectedShipmentStatusValue = shipmentsListFiltersStore.getSelectedShipmentStatusValue(getTranslation, scope);

    const onSelectShipmentStatus = (value: MultiValue<IOption>) => {
        if (!value?.length) {
            shipmentsListFiltersStore.setSelectedShipmentStatus(null);
            return;
        }

        const lastSelected = value[value.length - 1];
        
        if (lastSelected?.value === -1) {
            shipmentsListFiltersStore.setSelectedShipmentStatus(null);
            return;
        }
        
        const filteredValues = value.filter(v => v.value !== -1);
        const statuses = filteredValues
            .map(v => Number(v.value) as SHIPMENT_STATUS_ENUM);
        // When the user selects received, we also want to show verified statuses, cause they have the same label.
        if (statuses.includes(SHIPMENT_STATUS_ENUM.RECEIVED)) {
            statuses.push(SHIPMENT_STATUS_ENUM.VERIFIED);
        }
        shipmentsListFiltersStore.setSelectedShipmentStatus(statuses);
    };

    const onSelectCarrierId = (value: MultiValue<IOption>) => {
        if (!value?.length) {
            shipmentsListFiltersStore.setSelectedCarrierId(null);
            return;
        }

        const lastSelected = value[value.length - 1];
        
        if (lastSelected?.value === 0) {
            shipmentsListFiltersStore.setSelectedCarrierId(null);
            return;
        }
        
        const filteredValues = value.filter(v => v.value !== 0);
        const carriers = filteredValues.map(v => v.value as string);
        
        shipmentsListFiltersStore.setSelectedCarrierId(carriers);
    };


    const onSelectCreateDate = (dateRange: IDateRangeISO | null) => {
        if (!dateRange?.from || !dateRange?.to) {
            shipmentsListFiltersStore.setSelectedCreateDate('');
            return;
        }

        shipmentsListFiltersStore.setSelectedCreateDate(`${dateRange.from}/${dateRange.to}`);
    };

    const onSelectOrderOrRmaCreateDate = (dateRange: IDateRangeISO | null) => {
        if (!dateRange?.from || !dateRange?.to) {
            shipmentsListFiltersStore.setSelectedOrderOrRmaCreateDate('');
            return;
        }

        shipmentsListFiltersStore.setSelectedOrderOrRmaCreateDate(`${dateRange.from}/${dateRange.to}`);
    };

    const onSelectDeliveryDate = (dateRange: IDateRangeISO | null) => {
        if (!dateRange?.from || !dateRange?.to) {
            shipmentsListFiltersStore.setSelectedDeliveryDate('');
            return;
        }

        shipmentsListFiltersStore.setSelectedDeliveryDate(`${dateRange.from}/${dateRange.to}`);
    };

    const onSelectLastUpdateDate = (dateRange: IDateRangeISO | null) => {
        if (!dateRange?.from || !dateRange?.to) {
            shipmentsListFiltersStore.setSelectedLastUpdateDate('');
            return;
        }

        shipmentsListFiltersStore.setSelectedLastUpdateDate(`${dateRange.from}/${dateRange.to}`);
    };

    const handleClose = () => {
        shipmentsListFiltersStore.discardChanges();
        onClose();
    };

    return (
        <Drawer closeButton={{ onClick: handleClose }}>
            <DefaultDrawerLayout
            drawerHeader={
            <div className="shipments-list-drawer-title">
                <IconFunnel color="var(--color-primary-2)" />
                <FontHeading5 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                    {'Filters'}
                </FontHeading5>
            </div>
        }
            drawerBody={
                <div className="shipments-list-drawer-content">
                    <FormField label={'Status'}>
                        <SelectDropdownMulti
                            isLoading={shipmentsListFiltersStore.isLoading}
                            options={shipmentStatusOptions}
                            value={selectedShipmentStatusValue}
                            onChange={onSelectShipmentStatus}
                        />
                    </FormField>
                    <FormField label={'Carrier'}>
                        <SelectDropdownMulti
                            isLoading={shipmentsListFiltersStore.isLoading}
                            options={shipmentsListFiltersStore.carrierIdOptions}
                            value={shipmentsListFiltersStore.selectedCarrierIdValue}
                            onChange={onSelectCarrierId}
                        />
                    </FormField>

                    <div className="shipments-list-filters-group">
                        <FontBody1 
                            color={FONT_COLOR.NEUTRAL10} 
                            variant={FONT_VARIANT.DEFAULT}>
                            Dates
                        </FontBody1>

                        <FormField label={'Shipment Created Date'}>
                            <DateRangePickerV2
                                onChange={onSelectCreateDate}
                                value={parseIsoDateRange(shipmentsListFiltersStore.createDate)}
                            />
                        </FormField>
                        
                        <FormField label={'RMA Created Date'}>
                            <DateRangePickerV2
                                onChange={onSelectOrderOrRmaCreateDate}
                                value={parseIsoDateRange(shipmentsListFiltersStore.orderOrRmaCreateDate)}
                            />
                        </FormField>
                        <FormField label={'Delivery Date'}>
                            <DateRangePickerV2
                                onChange={onSelectDeliveryDate}
                                value={parseIsoDateRange(shipmentsListFiltersStore.deliveryDate)}
                            />
                        </FormField>
                        <FormField label={'Date of Last Update'}>
                            <DateRangePickerV2
                                onChange={onSelectLastUpdateDate}
                                value={parseIsoDateRange(shipmentsListFiltersStore.lastUpdateDate)}
                            />
                        </FormField>
                    </div>
                </div>
            }
            drawerFooter={
                <div className="shipments-list-drawer-content">
                    <Button 
                        text={'Apply'}
                        onClick={() => {
                            onClose();
                            shipmentsListFiltersStore.commitChanges();
                            shipmentsStore.loadPage(1);
                        }}
                        disabled={!vGroup.isValid()}
                    />
                </div>
            }
        />
        </Drawer>
    )
});

export default ShipmentsListFiltersDrawer;
